import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Images, 
  Upload, 
  Calendar, 
  Users, 
  TrendingUp, 
  Clock,
  ArrowRight,
  Star,
  LogOut,
  ExternalLink,
  AlertTriangle,
  AlertCircle,
  Info,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Thumbnail-Komponente mit Fehlerbehandlung
const ImageThumbnail = ({ image, token }) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const thumbnailUrl = `${API}/images/${image.id}/thumbnail?t=${token}`;
  
  if (error) {
    return (
      <div className="w-full h-full bg-slate-200 flex items-center justify-center">
        <Images className="w-6 h-6 text-slate-400" />
      </div>
    );
  }
  
  return (
    <>
      {loading && (
        <div className="absolute inset-0 bg-slate-200 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img
        src={thumbnailUrl}
        alt={image.original_filename || 'Bild'}
        className={`w-full h-full object-cover transition-opacity ${loading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setLoading(false)}
        onError={() => {
          console.error('Image load error:', image.id);
          setError(true);
          setLoading(false);
        }}
      />
    </>
  );
};

const DashboardPage = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    if (isAdmin()) {
      fetchAlerts();
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const dismissAlert = (index) => {
    setDismissedAlerts(prev => [...prev, index]);
  };

  const handleAlertAction = (action) => {
    if (action === 'settings') {
      navigate('/admin/settings');
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error': return AlertTriangle;
      case 'warning': return AlertCircle;
      default: return Info;
    }
  };

  const getAlertStyles = (type) => {
    switch (type) {
      case 'error': return 'border-red-500 bg-red-50 text-red-900';
      case 'warning': return 'border-amber-500 bg-amber-50 text-amber-900';
      default: return 'border-blue-500 bg-blue-50 text-blue-900';
    }
  };

  const visibleAlerts = alerts.filter((_, index) => !dismissedAlerts.includes(index));

  const StatCard = ({ icon: Icon, title, value, description, color = 'primary' }) => (
    <Card className="card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}/10`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getGuestBadge = (uploadCount) => {
    if (uploadCount >= 5) return { label: '5+ Uploads', color: 'bg-purple-100 text-purple-800' };
    if (uploadCount >= 3) return { label: '3+ Uploads', color: 'bg-blue-100 text-blue-800' };
    if (uploadCount >= 2) return { label: '2+ Uploads', color: 'bg-green-100 text-green-800' };
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 heading-app">Dashboard</h1>
          <p className="text-slate-500">Willkommen zurück, {user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" target="_blank">
            <Button variant="outline" size="sm" data-testid="view-website-button">
              <ExternalLink className="w-4 h-4 mr-2" />
              Website ansehen
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="header-logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </div>

      {/* System Alerts */}
      {visibleAlerts.length > 0 && (
        <div className="space-y-3" data-testid="dashboard-alerts">
          {visibleAlerts.map((alert, index) => {
            const AlertIcon = getAlertIcon(alert.type);
            return (
              <Alert 
                key={index} 
                className={`relative ${getAlertStyles(alert.type)}`}
              >
                <AlertIcon className="h-5 w-5" />
                <div className="flex-1">
                  <AlertTitle className="font-semibold">{alert.title}</AlertTitle>
                  <AlertDescription className="mt-1">
                    {alert.message}
                  </AlertDescription>
                  {alert.action && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => handleAlertAction(alert.action)}
                      data-testid={`alert-action-${index}`}
                    >
                      {alert.action_label}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
                <button
                  onClick={() => dismissAlert(index)}
                  className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/10 transition-colors"
                  data-testid={`dismiss-alert-${index}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </Alert>
            );
          })}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          icon={Images}
          title="Gesamte Bilder"
          value={stats?.total_images || 0}
          color="primary"
        />
        <StatCard 
          icon={Upload}
          title="Uploads heute"
          value={stats?.uploads_today || 0}
          color="green-600"
        />
        <StatCard 
          icon={Calendar}
          title="Uploads diesen Monat"
          value={stats?.uploads_month || 0}
          color="blue-600"
        />
        <StatCard 
          icon={Clock}
          title="Neue Bilder"
          value={stats?.new_images || 0}
          description="Noch nicht heruntergeladen"
          color="amber-600"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard 
          icon={Users}
          title="Wiederkehrende Gäste"
          value={stats?.recurring_guests || 0}
          description="Mit 2+ Uploads"
          color="purple-600"
        />
        <StatCard 
          icon={TrendingUp}
          title="Nicht verwendet"
          value={stats?.unused_images || 0}
          description="Bilder ohne Verwendung"
          color="slate-600"
        />
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Top Gäste</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.top_guests?.length || 0}</p>
                <p className="text-sm text-slate-500 mt-1">Aktivste Einsender</p>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-100">
                <Star className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Images */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg heading-app">Neueste Bilder</CardTitle>
              <CardDescription>Zuletzt hochgeladene Bilder</CardDescription>
            </div>
            <Link to="/admin/media">
              <Button variant="ghost" size="sm" data-testid="view-all-images-button">
                Alle anzeigen
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_images?.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {stats.recent_images.slice(0, 8).map((image) => (
                  <Link 
                    key={image.id} 
                    to={`/admin/media/${image.id}`}
                    className="aspect-square rounded-lg overflow-hidden bg-slate-100 gallery-image relative"
                  >
                    <ImageThumbnail image={image} token={token} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Images className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Noch keine Bilder vorhanden</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Guests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg heading-app">Top Gäste</CardTitle>
              <CardDescription>Gäste mit den meisten Uploads</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.top_guests?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_guests.map((guest, index) => {
                  const badge = getGuestBadge(guest.total_uploads);
                  return (
                    <div 
                      key={guest.id || index}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {index + 1}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{guest.name}</p>
                          <p className="text-sm text-slate-500">{guest.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {badge && (
                          <Badge className={badge.color}>{badge.label}</Badge>
                        )}
                        <span className="text-sm font-semibold text-slate-700">
                          {guest.total_uploads} Bilder
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Noch keine Gäste vorhanden</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
